/**
 * CustomChat Component
 *
 * A custom player-to-player chat component that replaces Empirica's default Chat.
 * This component was created to avoid issues with the default Chat's avatar rendering
 * and to provide better control over message styling and alignment.
 *
 * Features:
 * - Proper left/right message alignment (user on right, others on left)
 * - Clean bubble interface without /avatar text issues
 * - Indigo/purple gradient for user messages
 * - Auto-scrolling to latest messages
 * - Timestamps for each message
 * - Enter key support for sending messages
 * - Real-time typing indicator for other players
 * - LaTeX formula rendering with $...$ syntax
 * - Formula reference display (when formulas is a string)
 * - Optional formula palette for inserting pre-defined formulas (when formulas is an array)
 *
 * Props:
 * - scope: The Empirica scope (usually round) where messages are stored
 * - attribute: The attribute name to use for storing messages (e.g., "chat")
 * - formulas: Either a string (displayed as reference) or array of { label, latex, description } (clickable palette)
 *
 * Usage:
 * <CustomChat scope={round} attribute="chat" />
 * <CustomChat scope={round} attribute="chat" formulas={[{ label: "sin", latex: "\\sin(\\theta)" }]} />
 *
 * Message Format:
 * {
 *   text: string,
 *   playerId: string,
 *   timestamp: number
 * }
 */

import React, { useEffect, useRef, useCallback, useState } from "react";
import { usePlayer, usePlayers } from "@empirica/core/player/classic/react";
import { TypingIndicator } from "@/components/feedback/TypingIndicator";
import { FormulaPalette } from "@/components/chat/FormulaPalette";
import { LatexMessage } from "@/components/chat/LatexMessage";
import { FormulaReference } from "@/components/chat/FormulaReference";

export function CustomChat({ scope, attribute, formulas = [] }) {
  const player = usePlayer();
  const players = usePlayers();
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingSaveRef = useRef(0); // Throttle DB saves

  // Get messages from the scope
  const messages = scope.get(attribute) || [];

  // Formula reference/palette is shown only to the Teacher (Alice, marker 0);
  // the Student's chat does not show formulas.
  const isTeacher = player.get("marker") === 0;

  // Check if partner is typing
  const partner = players?.find(p => p.id !== player.id);
  const isPartnerTyping = partner?.get("isTyping") || false;

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, isPartnerTyping]);

  const [inputText, setInputText] = React.useState("");

  // Handle typing status - set isTyping when user types, clear after 2 seconds of inactivity
  // Throttled to save to DB at most every 3 seconds to reduce DB writes
  const handleTyping = useCallback((text) => {
    setInputText(text);

    const now = Date.now();

    // Only save isTyping:true to DB every 3 seconds (throttle)
    if (text.trim() && now - lastTypingSaveRef.current > 3000) {
      player.set("isTyping", true);
      lastTypingSaveRef.current = now;
    }

    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set new timeout to clear typing status
    typingTimeoutRef.current = setTimeout(() => {
      player.set("isTyping", false);
    }, 2000);
  }, [player]);

  // Clear typing status on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      player.set("isTyping", false);
    };
  }, [player]);

  const handleSend = () => {
    if (!inputText.trim()) return;

    // Clear typing status when sending
    player.set("isTyping", false);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    const newMessage = {
      text: inputText.trim(),
      playerId: player.id,
      timestamp: Date.now(),
    };

    // append() is server-side atomic. The previous read-modify-write
    // (scope.set(attribute, [...scope.get(attribute), msg])) silently dropped
    // a message whenever both players sent within one sync round-trip.
    scope.append(attribute, newMessage);
    setInputText("");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="custom-chat flex flex-col h-full bg-white">
      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
        {messages.length === 0 && (
          <div className="text-center text-gray-500 mt-8">
            <p className="font-medium">No messages yet</p>
            <p className="text-sm mt-2">Send a message to start the conversation.</p>
          </div>
        )}

        {messages.map((msg, index) => {
          const isCurrentPlayer = msg.playerId === player.id;

          return (
            <div
              key={index}
              className={`flex ${isCurrentPlayer ? "justify-end" : "justify-start"}`}
            >
              <div className={`max-w-[85%] ${isCurrentPlayer ? "items-end" : "items-start"} flex flex-col`}>
                <div className={`text-xs mb-1 px-3 ${isCurrentPlayer ? "text-right text-gray-600" : "text-left text-gray-600"}`}>
                  {isCurrentPlayer ? "You" : "Partner"}
                </div>
                <div
                  className={`rounded-2xl px-5 py-3 ${
                    isCurrentPlayer
                      ? "bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-br-sm"
                      : "bg-white text-gray-800 border border-gray-200 rounded-bl-sm shadow-sm"
                  }`}
                >
                  <div className="break-words text-base leading-relaxed">
                    <LatexMessage text={msg.text} />
                  </div>
                </div>
                {msg.timestamp && (
                  <div className={`text-xs text-gray-400 mt-1 px-3`}>
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Typing indicator */}
        <TypingIndicator isTyping={isPartnerTyping} partnerName="Partner" />

        <div ref={messagesEndRef} />
      </div>

      {/* Input area - expandable textarea like social media */}
      <div className="border-t border-gray-200 p-4 bg-white">
        {/* Formula reference/palette - Teacher only; shown if formulas are provided */}
        {isTeacher && formulas && formulas.length > 0 && (
          <div className="mb-3">
            {/* If formulas is a string, show as reference display (click to insert) */}
            {typeof formulas === "string" ? (
              <FormulaReference
                formulas={formulas}
                onInsert={(latex) => {
                  setInputText((prev) => prev + (prev && !prev.endsWith(" ") ? " " : "") + latex + " ");
                }}
              />
            ) : (
              /* If formulas is an array, show clickable palette */
              <FormulaPalette
                formulas={formulas}
                onInsert={(latex) => {
                  // Insert formula at cursor position or append to end
                  setInputText((prev) => prev + (prev && !prev.endsWith(" ") ? " " : "") + latex + " ");
                }}
              />
            )}
          </div>
        )}
        <div className="flex space-x-2 items-end">
          <textarea
            value={inputText}
            onChange={(e) => handleTyping(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Type your message..."
            rows={1}
            className="flex-1 border border-gray-300 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none overflow-hidden"
            style={{
              minHeight: '48px',
              maxHeight: '150px',
              height: 'auto',
            }}
            onInput={(e) => {
              // Auto-expand textarea as user types
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
            }}
          />
          <button
            onClick={handleSend}
            disabled={!inputText.trim()}
            className="bg-indigo-500 text-white px-6 py-3 rounded-xl hover:bg-indigo-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium text-base flex-shrink-0"
          >
            Send
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2 text-center">Press Enter to send, Shift+Enter for new line</p>
      </div>
    </div>
  );
}